/**
 * Derive album-style titles from photo caption first lines
 * (e.g. "2025, Pt. 1", "May 2026", "September & October, 2024").
 */

const MONTH =
  '(January|February|March|April|May|June|July|August|September|October|November|December)';

const ALBUM_TITLE_PATTERNS: RegExp[] = [
  new RegExp(`^${MONTH}\\s+\\d{4}$`, 'i'),
  new RegExp(
    `^${MONTH}\\s+\\d{1,2}(?:\\s*[-–]\\s*\\d{1,2})?,\\s*\\d{4}$`,
    'i',
  ),
  /^\d{4},\s*Pt\.?\s*\d+$/i,
  /^\d{4}\s+Overflow$/i,
  /^#?\d{4}\s+overflow$/i,
  /^(Spring|Summer|Fall|Autumn|Winter)\s+\d{4}$/i,
  new RegExp(`^${MONTH}\\s*[/&]\\s*${MONTH},?\\s*\\d{4}$`, 'i'),
  new RegExp(
    `^${MONTH},\\s*${MONTH},?\\s*(?:&|and)\\s*${MONTH}\\s+\\d{4}$`,
    'i',
  ),
  new RegExp(`^${MONTH}\\s*&\\s*${MONTH},\\s*\\d{4}$`, 'i'),
];

/** Strip tags / breaks into plain lines (blank lines kept for paragraph gaps). */
function photoCaptionLinesPreserveBlanks(
  input: string | undefined | null,
): string[] {
  if (!input) return [];
  let s = String(input);
  for (let i = 0; i < 4; i++) {
    const next = s.replace(/&amp;/gi, '&');
    if (next === s) break;
    s = next;
  }
  s = s
    .replace(/\r\n/g, '\n')
    .replace(/&#10;/g, '\n')
    .replace(/&nbsp;/gi, ' ')
    .replace(/<\/p>/gi, '\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/\u00a0/g, ' ')
    // Invisible separators / bidi marks that Instagram sometimes embeds
    .replace(/[\u200b-\u200f\u2060\u2063\ufeff]/g, '');
  return s.split('\n').map((l) => l.replace(/[ \t]+$/g, '').trim());
}

/** Strip tags / breaks into non-empty plain lines (caption structure preserved). */
export function photoCaptionPlainLines(input: string | undefined | null): string[] {
  return photoCaptionLinesPreserveBlanks(input).filter(Boolean);
}

/** Normalize a candidate album title for matching / display. */
function normalizeAlbumTitleCandidate(line: string): string {
  return line
    .replace(/[\u200b-\u200f\u2060\u2063\ufeff]/g, '')
    .replace(/[.…]+$/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** True when a caption first line is a period / series album label. */
export function isPhotoAlbumTitleLine(line: string): boolean {
  const t = normalizeAlbumTitleCandidate(line);
  if (!t || t.length > 60) return false;
  if (!/\b(?:19|20)\d{2}\b/.test(t)) return false;
  if (t.startsWith('@')) return false;
  return ALBUM_TITLE_PATTERNS.some((re) => re.test(t));
}

export type ParsedPhotoCaption = {
  /** Album title from the caption first line, or null if none. */
  albumTitle: string | null;
  /** Remaining caption text with the album title line removed (when present). */
  body: string;
};

/**
 * Drop a leading caption line that matches `title` (case-insensitive), plus any
 * blank lines immediately under it, so the body starts on the next paragraph.
 */
export function stripLeadingPhotoTitle(
  description: string | undefined | null,
  title: string | undefined | null,
): string {
  const raw = String(description || '');
  const titleNorm = normalizeAlbumTitleCandidate(String(title || '')).replace(
    /^#/,
    '',
  );
  if (!titleNorm) return raw.trim();

  const lines = photoCaptionLinesPreserveBlanks(raw);
  const firstIdx = lines.findIndex((l) => l.trim());
  if (firstIdx < 0) return '';

  const firstNorm = normalizeAlbumTitleCandidate(lines[firstIdx]!).replace(
    /^#/,
    '',
  );
  if (firstNorm.toLowerCase() !== titleNorm.toLowerCase()) {
    return raw.trim();
  }

  let i = firstIdx + 1;
  while (i < lines.length && !lines[i]!.trim()) i += 1;
  return lines.slice(i).join('\n').trim();
}

/**
 * Split a photo description into an optional album title + remaining body.
 * When a title matches, it is removed from the body (including a blank line
 * under it) so the description starts on the following paragraph.
 */
export function parsePhotoCaption(
  description: string | undefined | null,
): ParsedPhotoCaption {
  const raw = String(description || '');
  const lines = photoCaptionPlainLines(raw);
  const first = lines[0] || '';
  if (!first || !isPhotoAlbumTitleLine(first)) {
    return { albumTitle: null, body: raw.trim() };
  }

  const albumTitle = normalizeAlbumTitleCandidate(first).replace(/^#/, '');
  return {
    albumTitle,
    body: stripLeadingPhotoTitle(raw, albumTitle),
  };
}

/** Display title: caption album label, else stored post title. */
export function photoDisplayTitle(
  post: Pick<{ title: string; description?: string }, 'title' | 'description'>,
): string {
  const { albumTitle } = parsePhotoCaption(post.description);
  return albumTitle || post.title?.trim() || 'Photo';
}

/** Long-form post date for the photo detail subtext. */
export function formatPhotoPostDate(date: string | undefined | null): string {
  if (!date?.trim()) return '';
  const raw = date.trim();
  // Catalog stores MM/DD/YY (or MM/DD/YYYY)
  const m = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  let d: Date | null = null;
  if (m) {
    const year = m[3]!.length === 2 ? 2000 + Number(m[3]) : Number(m[3]);
    d = new Date(year, Number(m[1]) - 1, Number(m[2]));
  } else {
    const parsed = new Date(raw);
    if (Number.isFinite(parsed.getTime())) d = parsed;
  }
  if (!d || !Number.isFinite(d.getTime())) return raw;
  return d.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}
